import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import TagManager from "react-gtm-module";

import { getLocations } from "../lib/locations";
import { getFAQCategory } from "../lib/faq";

import Button from "../components/button";
import CollapsedQuestion from "../components/collapsed-question";
import Divider from "../components/divider";
import FeaturedImageSection from "../components/featured-image-section";
import FeaturedLocationsSection from "../components/featured-locations-section";
import Hero from "../components/hero";
import Input from "../components/input";
import Select from "../components/select";
import Stats from "../components/stats";

import styles from "./brokers.module.scss";
import config from "../config";

import SeoMeta from "../components/seo-meta";
import { getSEOPageProps } from "../lib/seo";
import { activeCampaignSubmit } from "../lib/forms";

const fieldMap = {
  memberFirstName: "field[31]",
  memberLastName: "field[32]",
  memberEmail: "field[33]",
  referralFirstName: "firstname",
  referralLastName: "lastname",
  referralEmail: "email",
  membership: "field[35]",
  location: "field[36]",
};

export default function ReferralProgram({ locations, questions, seo }) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { register, formState, handleSubmit, watch } = useForm({
    mode: "onChange",
    // Default Active Campaign values for this
    // specific form.
    defaultValues: {
      u: "10",
      f: "10",
      s: "0",
      c: "0",
      m: "0",
      act: "sub",
      v: "2",
      or: "daf320c4ff38fc956841bf9171933007",
    },
  });

  watch();

  const onSubmit = (data) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "contact_us",
        type: "referral",
        ...data,
      },
    });

    activeCampaignSubmit(data, false)
      .then(() => setSubmitted(true))
      .catch((e) => setError(e));
  };

  const onError = (e) => console.log(e);

  const { errors } = formState;

  const getError = (field) => {
    const fieldNum = field.split("[")[1].slice(0, -1);

    if (errors.field && errors.field[fieldNum]) {
      return errors.field[fieldNum].message;
    }

    return null;
  };

  return (
    <>
      <div className="container">
        <SeoMeta {...seo} />
        <Hero
          title="Referral Program"
          description="Find out how you can leverage the East Room platform and network to earn discounted rates on your existing membership."
        />

        <figure className={styles.FeaturedImage}>
          <Image
            src="/images/referral-program.jpg"
            alt="Referral program"
            width="1240"
            height="679"
            layout="responsive"
            objectFit="cover"
          />
        </figure>

        <Stats
          stats={[
            {
              number: "90%",
              description:
                "of people using coworking spaces report to be happier because of it",
              link: "source: Harvard Business Review “Coworking is Not About Workspace - its about feeling less lonely”, 2017",
            },
            {
              number: "68%",
              description:
                "of North American workers prefer a hybrid workplace model.",
              link: "source: Ergonomic Trends “ Return to Work Statistics”, 2021",
            },
            {
              number: "22%",
              description:
                "of the North American workforce is estimated to be working in flexible office solutions by 2025.",
              link: "source: Global Workplace Analytics “Telework in the 21st Century”, 2021",
            },
          ]}
        />

        <div className={styles.SectionWrapper}>
          <FeaturedImageSection
            image="/images/private-space.png"
            desktopImage={
              <Image
                src={"/images/private-space-axo.jpg"}
                alt={"Design forward, flexible solutions "}
                width="536"
                height="527"
                layout="responsive"
              />
            }
            mobileImage={
              <Image
                src={"/images/private-space-axo.jpg"}
                alt={"Design forward, flexible solutions"}
                width="335"
                height="329"
                layout="responsive"
              />
            }
            title="Design forward, flexible solutions"
            description="Whether you’re referring an independent professional or established enterprise, East Room has a variety of office solutions to meet their business needs."
            callToAction={
              <Link href="/solutions" passHref>
                <a>
                  <Button outline rounded small>
                    Explore our solutions
                  </Button>
                </a>
              </Link>
            }
          />
        </div>
      </div>

      <FeaturedLocationsSection locations={locations} />

      <div className="container">
        <div className={styles.BrokerFormWrapper}>
          <div className={styles.BrokerFormContent}>
            <h2 className="font-bold">Submit a referral</h2>

            <p>
              Submit the form and one of our sales representatives will contact
              your referral to discover how they can leverage the East Room
              network and platform.
            </p>
          </div>

          {submitted && (
            <p className={styles.ConfirmationMessage}>
              Thank you, we will be in touch shortly!
            </p>
          )}

          {!submitted && (
            <form
              className={styles.BrokerForm}
              onSubmit={handleSubmit(onSubmit, onError)}
            >
              <div className="flex flex-row justify-between items-center gap-2">
                <Input
                  type="text"
                  placeholder="Member first name *"
                  {...register(fieldMap.memberFirstName, {
                    required: "Member first name is required",
                  })}
                  errorText={getError(fieldMap.memberFirstName)}
                  inline
                />
                <Input
                  type="text"
                  placeholder="Member last name *"
                  {...register(fieldMap.memberLastName, {
                    required: "Member last name is required",
                  })}
                  errorText={getError(fieldMap.memberLastName)}
                  inline
                />
              </div>
              <div>
                <Input
                  type="text"
                  placeholder="Member work email address *"
                  {...register(fieldMap.memberEmail, {
                    required: "Member email is required",
                  })}
                  errorText={getError(fieldMap.memberEmail)}
                />
              </div>
              <div className="flex flex-row justify-between items-center gap-2">
                <Input
                  type="text"
                  placeholder="Referral first name *"
                  {...register("firstname", {
                    required: "Referral first name is required",
                  })}
                  errorText={errors.firstname && errors.firstname.message}
                  inline
                />
                <Input
                  type="text"
                  placeholder="Referral last name *"
                  {...register("lastname", {
                    required: "Referral last name is required",
                  })}
                  errorText={errors.lastname && errors.lastname.message}
                  inline
                />
              </div>
              <div>
                <Input
                  type="text"
                  placeholder="Referral work email address *"
                  {...register("email", {
                    required: "Referral email is required",
                  })}
                  errorText={errors.email && errors.email.message}
                />
              </div>

              <div>
                <Select
                  type="text"
                  {...register(fieldMap.membership, {
                    required: "Membership is required",
                  })}
                  errorText={getError(fieldMap.membership)}
                >
                  <option value="" disabled selected>
                    Which membership is your referral most interested in? *
                  </option>
                  <option value="Mailbox">Mailbox</option>
                  <option value="Social Membership">Social Membership</option>
                  <option value="Club Membership">Club Membership</option>
                  <option value="Dedicated Desk">Dedicated Desk</option>
                  <option value="2 Person Office">2 Person Office</option>
                  <option value="Standard Office">Standard Office</option>
                  <option value="Office Suite">Office Suite</option>
                  <option value="Premium Office">Premium Office</option>
                  <option value="Undecided">Undecided</option>
                </Select>
              </div>

              <div>
                <Select
                  type="text"
                  placeholder=""
                  {...register(fieldMap.location, {
                    required: "Location is required",
                  })}
                  errorText={getError(fieldMap.location)}
                >
                  <option value="" disabled selected>
                    Select your referral&apos;s location of interest *
                  </option>
                  {locations.map((location) => (
                    <option
                      value={`${location.address1}, ${location.city} ${location.province}`}
                      key={location.slug}
                    >
                      {location.address1}, {location.city} {location.province}
                    </option>
                  ))}
                </Select>
              </div>

              {error && <p>{error}</p>}

              <div className={styles.BrokerFormButtonWrapper}>
                <Button rounded outline small type="submit">
                  Get in Touch
                </Button>
              </div>
            </form>
          )}
        </div>

        <Divider />

        <section className={styles.BrokerQuestionsSection}>
          <div className={styles.BrokerQuestionHeader}>
            <h3>Have a question?</h3>
          </div>
          <div className={styles.BrokerQuestionContent}>
            {questions.map((question) => (
              <CollapsedQuestion
                key={question.title}
                question={question.title}
                answer={question.description}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

export async function getStaticProps() {
  try {
    const [locations, questions, seo] = await Promise.all([
      getLocations(),
      getFAQCategory("referral-program"),
      getSEOPageProps({ resolvedUrl: "/referral-program" }),
    ]);

    return {
      props: {
        locations,
        questions,
        seo,
      },
      revalidate: config.ISR_TIMING,
    };
  } catch (e) {
    console.log(e.message);
  }
}
